import { 
  getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, 
  collection, query, where, getDocs, writeBatch, onSnapshot
} from "firebase/firestore";
import { db } from "../firebase";
import { Tenant, ParsedMenuData, VisitedTenant } from "../types";

// Keep this one around but ideally it should just not be exported circularly
export const getTenantId = (): string => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');
    if (shopParam) return shopParam;
  }
  const stored = localStorage.getItem('APP_TENANT_ID');
  if (stored) return stored;
  return ''; 
};

export const getAdminTenant = async (email: string): Promise<string | null> => {
    const q = query(collection(db, "admins"), where("email", "==", email.toLowerCase().trim()));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    return snapshot.docs[0].data().tenantId || null;
};

export const assignAdminToTenant = async (email: string, tenantId: string) => {
    const q = query(collection(db, "admins"), where("email", "==", email.toLowerCase().trim()));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
        await updateDoc(snapshot.docs[0].ref, { tenantId });
    } else {
        await setDoc(doc(collection(db, "admins")), {
            email: email.toLowerCase().trim(),
            tenantId
        });
    }
};

export const getAllTenants = async (): Promise<Tenant[]> => {
    try {
        let allTenants: Tenant[] = [];

        try {
            const saasSnapshot = await getDocs(collection(db, 'saas_tenants'));
            const list3 = saasSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Tenant));
            allTenants = [...allTenants, ...list3];
        } catch (e) { console.log('saas_tenants fetch error', e); }

        try {
            const regSnapshot = await getDocs(collection(db, 'tenantsRegistry'));
            const list2 = regSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Tenant));
            allTenants = [...allTenants, ...list2];
        } catch (e) { console.log('tenantsRegistry fetch error', e); }

        try {
            const snapshot = await getDocs(collection(db, 'tenants'));
            const list1 = snapshot.docs.map(d => {
                const data = d.data() as any;
                const tId = data.slug || d.id;
                return { id: tId, ...data } as Tenant;
            });
            allTenants = [...allTenants, ...list1];
        } catch (e) { console.log('tenants fetch error', e); }
        
        // Remove duplicates and merge properties cleanly
        const unique = new Map<string, Tenant>();
        allTenants.forEach(t => {
            const existing = unique.get(t.id) || {};
            unique.set(t.id, { ...existing, ...t });
        });

        return Array.from(unique.values());
    } catch(e) {
        console.error("getAllTenants ERROR: ", e);
        return [];
    }
};

export const createTenant = async (name: string, slug: string, city: string, isRetail: boolean, customDomains: string[]) => {
    const newDoc = doc(db, 'tenants', slug);
    await setDoc(newDoc, {
        name,
        slug,
        city,
        type: isRetail ? 'retail' : 'food',
        isActive: true,
        createdAt: Date.now(),
        customDomains: customDomains || []
    });
};

export const incrementTenantOrderStats = async (id: string) => {
    if (!id || id === 'root') return;
    try {
        const { doc, setDoc, increment } = await import('firebase/firestore');
        const ref = doc(db, 'tenants', id);
        await setDoc(ref, {
            totalOrders: increment(1),
            lastOrderDate: Date.now()
        }, { merge: true });
    } catch (e) {
        console.error("Failed to increment tenant stats:", e);
    }
};

export const updateTenantInfo = async (id: string, data: Partial<Tenant>) => {
    const updateData = { ...data };
    
    // Remove undefined values
    Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof Tenant] === undefined) {
            delete updateData[key as keyof Tenant];
        }
    });

    if (updateData.customDomains) {
        updateData.customDomains = updateData.customDomains.map((d: string) => d.toLowerCase().trim()).filter(Boolean);
    }
    await setDoc(doc(db, 'tenants', id), updateData, { merge: true });
};

export const updateTenantStatus = async (id: string, isActive: boolean) => {
    await setDoc(doc(db, 'tenants', id), { isActive }, { merge: true });
};

export const deleteTenant = async (id: string): Promise<string[]> => {
    const errors: string[] = [];
    const _db = getFirestore();
    const batch = writeBatch(_db);
    try {
        const collectionsToClear = ['products', 'categories', 'ingredients', 'menus', 'live_orders', 'customers'];
        for (const colName of collectionsToClear) {
            const snapshot = await getDocs(collection(_db, `tenants/${id}/${colName}`));
            snapshot.docs.forEach(d => batch.delete(d.ref));
        }
        batch.delete(doc(_db, 'tenants', id));
        batch.delete(doc(_db, `tenants/${id}/settings/global`));
        batch.delete(doc(_db, `tenants/${id}/settings/delivery`));
        batch.delete(doc(_db, `tenants/${id}/settings/graphics`));
        await batch.commit();
    } catch (e: any) {
        errors.push(e.message || 'Error occurred');
    }
    return errors;
};

export const getTenantMetadata = async (id: string): Promise<Tenant | null> => {
    try {
        const docSnap = await getDoc(doc(db, 'tenants', id));
        if (docSnap.exists()) {
            return { id: docSnap.id, ...(docSnap.data() as any) } as Tenant;
        }
        return null;
    } catch (e) {
        return null;
    }
};

export const resolveTenantIdFromDomain = async (domain: string): Promise<string | null> => {
    try {
        const qDomain = query(collection(db, 'tenants'), where('customDomains', 'array-contains', domain));
        const snapDomain = await getDocs(qDomain);
        if (!snapDomain.empty) {
            return snapDomain.docs[0].id;
        }
        const qSlug = query(collection(db, 'tenants'), where('slug', '==', domain.replace('.ordinaora.it', '')));
        const snapSlug = await getDocs(qSlug);
        if (!snapSlug.empty) {
            return snapSlug.docs[0].id;
        }
        return null;
    } catch (e) {
        return null;
    }
};

export const initializeTenantStructure = async (tenantId: string, name: string, isRetail: boolean) => {
    await setDoc(doc(db, 'tenants', tenantId), {
        name,
        isActive: true,
        type: isRetail ? 'retail' : 'food',
        createdAt: Date.now()
    }, { merge: true });
};

export const saveTenantLogo = async (tenantId: string, logoUrl: string) => {
    await updateDoc(doc(db, 'tenants', tenantId), {
        logoUrl
    });
};

export const addVisitedTenant = (id: string, name: string) => {
    try {
        const visited = JSON.parse(localStorage.getItem('visited_tenants') || '[]');
        const filtered = visited.filter((v: any) => v.id !== id);
        filtered.unshift({ id, name, timestamp: Date.now() });
        if (filtered.length > 5) filtered.length = 5;
        localStorage.setItem('visited_tenants', JSON.stringify(filtered));
    } catch(e) {}
};

export const getVisitedTenants = (): VisitedTenant[] => {
    try {
        return JSON.parse(localStorage.getItem('visited_tenants') || '[]');
    } catch(e) {
        return [];
    }
};

interface CityTenantsOptions {
    city: string;
    type?: 'food' | 'retail';
    center?: { lat: number; lng: number };
    radiusKm?: number;
}

export const subscribeToAllTenants = (callback: (tenants: Tenant[]) => void) => {
    const listMap = new Map<string, any>();
    const regMap = new Map<string, any>();
    const saasMap = new Map<string, any>();
    
    let unsubTenants = () => {};
    let unsubRegistry = () => {};
    let unsubSaas = () => {};
    
    const triggerCallback = () => {
        const enrichedTenants: Tenant[] = [];
        
        const allIds = new Set([
            ...listMap.keys(),
            ...regMap.keys(),
            ...saasMap.keys()
        ]);
        
        allIds.forEach((tId) => {
            const tenantData = listMap.get(tId) || {};
            const registryData = regMap.get(tId) || {};
            const saasData = saasMap.get(tId) || {};
            
            const combined = {
                ...tenantData,
                ...registryData,
                ...saasData,
                id: tId
            };
            enrichedTenants.push(combined as Tenant);
        });
        
        callback([...enrichedTenants]);
    };
    
    // Subscribe to tenants
    unsubTenants = onSnapshot(collection(db, "tenants"), (snapshot: any) => {
        listMap.clear();
        snapshot.forEach((d: any) => {
            const data = d.data();
            const tId = data.slug || d.id;
            const existing = listMap.get(tId) || {};
            listMap.set(tId, { ...existing, ...data, id: tId });
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: tenants unsub error:", err));
    
    // Subscribe to tenantsRegistry
    unsubRegistry = onSnapshot(collection(db, "tenantsRegistry"), (snapshot: any) => {
        regMap.clear();
        snapshot.forEach((d: any) => {
            regMap.set(d.id, d.data());
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: tenantsRegistry unsub error:", err));
    
    // Subscribe to saas_tenants
    unsubSaas = onSnapshot(collection(db, "saas_tenants"), (snapshot: any) => {
        saasMap.clear();
        snapshot.forEach((d: any) => {
            saasMap.set(d.id, d.data());
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: saas_tenants unsub error:", err));
    
    return () => {
        unsubTenants();
        unsubRegistry();
        unsubSaas();
    };
};

export const subscribeToCityTenants = (optionsOrCity: string | CityTenantsOptions, callback: (tenants: Tenant[]) => void) => {
    const options: CityTenantsOptions = typeof optionsOrCity === 'string' 
        ? { city: optionsOrCity } 
        : optionsOrCity;
        
    const listMap = new Map<string, any>();
    const regMap = new Map<string, any>();
    const saasMap = new Map<string, any>();
    
    let unsubTenants = () => {};
    let unsubRegistry = () => {};
    let unsubSaas = () => {};
    
    const matchCity = (riderCityVal: string, tenantCityVal: string, tenantAddressVal: string): boolean => {
        const rc = (riderCityVal || '').toLowerCase().trim();
        const tc = (tenantCityVal || '').toLowerCase().trim();
        const ta = (tenantAddressVal || '').toLowerCase().trim();
        
        if (!rc) return true; // Direct pass if rider has no specified city
        if (!tc && !ta) return true; // Be inclusive if no city parameters are explicitly defined
        
        // Exact block check
        if (tc && (rc.includes(tc) || tc.includes(rc))) return true;
        if (ta && (rc.includes(ta) || ta.includes(rc))) return true;
        
        // Word level overlap check (e.g. "Pescara Montesilvano e limitrofi" contains "Pescara")
        const rcWords = rc.split(/[\s,e]+/).filter(w => w.length > 2);
        for (const word of rcWords) {
            if (tc && tc.includes(word)) return true;
            if (ta && ta.includes(word)) return true;
        }
        
        return false;
    };
    
    const triggerCallback = () => {
        const enrichedTenants: Tenant[] = [];
        
        // Combine all unique keys across all snapshots (tenants, tenantsRegistry, saas_tenants)
        const allIds = new Set([
            ...listMap.keys(),
            ...regMap.keys(),
            ...saasMap.keys()
        ]);
        
        allIds.forEach((tId) => {
            const tenantData = listMap.get(tId) || {};
            const registryData = regMap.get(tId) || {};
            const saasData = saasMap.get(tId) || {};
            
            const combined = {
                ...tenantData,
                ...registryData,
                ...saasData,
                id: tId
            };
            
            // Check active status (default to active if not explicit false)
            if (combined.isActive === false) return;
            
            // Filter out root or "nazionale" for riders
            const tCity = (combined.city || combined.shopAddress || '').toLowerCase().trim();
            if (tCity === 'nazionale' || combined.id === 'root' || tCity.includes('nazionale')) return;
            
            // Filter by city
            if (options.city) {
                const tenantCity = combined.city || '';
                const tenantAddress = combined.shopAddress || '';
                if (!matchCity(options.city, tenantCity, tenantAddress)) {
                    return;
                }
            }
            
            // Filter by type
            if (options.type && combined.type !== options.type) return;
            
            enrichedTenants.push(combined as Tenant);
        });
        
        if (navigator.geolocation) {
             navigator.geolocation.getCurrentPosition((pos) => {
                 const { latitude, longitude } = pos.coords;
                 
                 enrichedTenants.sort((a, b) => {
                     const aLoc: any = a.shopLatLng || (a as any).location;
                     const bLoc: any = b.shopLatLng || (b as any).location;
                     if (!aLoc && !bLoc) return 0;
                     if (!aLoc) return 1;
                     if (!bLoc) return -1;
                     
                     const distA = Math.sqrt(Math.pow(aLoc.lat - latitude, 2) + Math.pow(aLoc.lng - longitude, 2));
                     const distB = Math.sqrt(Math.pow(bLoc.lat - latitude, 2) + Math.pow(bLoc.lng - longitude, 2));
                     return distA - distB;
                 });
                 callback([...enrichedTenants]);
             }, () => {
                 callback(enrichedTenants);
             });
         } else {
             callback(enrichedTenants);
         }
    };
    
    // Subscribe to tenants
    unsubTenants = onSnapshot(collection(db, "tenants"), (snapshot: any) => {
        listMap.clear();
        snapshot.forEach((d: any) => {
            const data = d.data();
            const tId = data.slug || d.id;
            const existing = listMap.get(tId) || {};
            listMap.set(tId, { ...existing, ...data, id: tId });
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: tenants unsub error:", err));
    
    // Subscribe to tenantsRegistry
    unsubRegistry = onSnapshot(collection(db, "tenantsRegistry"), (snapshot: any) => {
        regMap.clear();
        snapshot.forEach((d: any) => {
            const data = d.data();
            regMap.set(d.id, data);
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: tenantsRegistry unsub error:", err));
    
    // Subscribe to saas_tenants
    unsubSaas = onSnapshot(collection(db, "saas_tenants"), (snapshot: any) => {
        saasMap.clear();
        snapshot.forEach((d: any) => {
            const data = d.data();
            saasMap.set(d.id, data);
        });
        triggerCallback();
    }, (err: any) => console.log("tenantService: saas_tenants unsub error:", err));
    
    return () => {
        unsubTenants();
        unsubRegistry();
        unsubSaas();
    };
};
